import { propertiesTypes, serializedTypes } from '@/reports/types';
import { commaFormatCurrency } from '@/utils/commaFormat/commaFormatCurrency';
import { formatDateFromReport } from '@/utils/formatDateFromReport/formatDateFromReport';

function isWithinLast12Months(date: Date): boolean {
  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
  return date >= twelveMonthsAgo;
}

export function sortArrayObjectsByValuationDate(
  values: propertiesTypes.RentalValues
) {
  return values
    .filter((value) =>
      isWithinLast12Months(new Date(value.valuation_date?.full || ''))
    )
    .sort((a, b) => {
      const valueA = new Date(a.valuation_date?.full || '').getTime();
      const valueB = new Date(b.valuation_date?.full || '').getTime();
      return valueA - valueB;
    });
}

export function sortArrayObjectsByFullDate(
  values: propertiesTypes.MarketValues
) {
  return values
    .filter((value) => isWithinLast12Months(new Date(value.date?.full || '')))
    .sort((a, b) => {
      const valueA = new Date(a.date?.full || '').getTime();
      const valueB = new Date(b.date?.full || '').getTime();
      return valueA - valueB;
    });
}

export function extractAndSortValuesBySource(
  marketValues: propertiesTypes.MarketValues,
  source: string
) {
  let filteredValues: propertiesTypes.MarketValues = [];
  if (marketValues.length) {
    filteredValues = marketValues.filter((m) => m.source === source);
  }
  return sortArrayObjectsByFullDate(filteredValues);
}

export const formatMarket = (
  marketValue: propertiesTypes.MarketValues[number] | undefined,
  priceRange: { minRange: number | null; maxRange: number | null } | null,
  property: serializedTypes.SerializedProperty
) => {
  const formattedValues = {
    date: '-',
    price: '-',
    range: '-',
    soldDate: '-' as string | undefined,
  };

  const marketDate = marketValue?.date?.full;
  if (marketDate) {
    const splittedDate = marketDate.split('-');
    formattedValues.date = `${splittedDate[1]}/${splittedDate[0]}`;
  }

  if (priceRange?.minRange) {
    formattedValues.range = commaFormatCurrency(priceRange.minRange);
  }

  if (priceRange?.maxRange) {
    formattedValues.range = priceRange?.minRange
      ? `${formattedValues.range} - `
      : '';
    formattedValues.range = `${formattedValues.range}${commaFormatCurrency(
      priceRange.maxRange
    )}`;
  }

  if (property.last_sold_price) {
    formattedValues.price = commaFormatCurrency(property.last_sold_price);
  }

  if (property.last_sold_date?.full) {
    const fullDate = property.last_sold_date.full || null;
    formattedValues.soldDate = formatDateFromReport(fullDate);
  }

  return formattedValues;
};

export const calculateMarketPricesRange = (
  valuesList: propertiesTypes.MarketValues,
  sources: string[]
) => {
  if (!valuesList.length) {
    return null;
  }

  const validMarketValues = valuesList.filter(
    (marketValue) => marketValue.total !== undefined || !!marketValue.total
  );

  const latestMarketValues: propertiesTypes.MarketValues = [];

  sources.forEach((source) => {
    const sourceValues = extractAndSortValuesBySource(
      validMarketValues,
      source
    );
    if (sourceValues[sourceValues.length - 1]) {
      latestMarketValues.push(sourceValues[sourceValues.length - 1]);
    }
  });

  const marketValuesPrices = latestMarketValues.map(
    (value) => value.total as number
  );

  if (!marketValuesPrices.length) {
    return null;
  }

  if (marketValuesPrices.length === 1) {
    return {
      minRange: marketValuesPrices[0],
      maxRange: null,
    };
  }

  return {
    minRange: Math.min(...marketValuesPrices),
    maxRange: Math.max(...marketValuesPrices),
  };
};

export const lastMarketValueUpdated = (
  marketList: propertiesTypes.MarketValues
) => {
  if (!marketList?.length) {
    return;
  }

  const marketsWithDate = marketList.filter((market) => !!market.date?.full);

  if (!marketsWithDate?.length) {
    return marketList[0];
  }

  return marketsWithDate.reduce(
    (a, b) => {
      const aDate = a?.date?.full;
      const bDate = b?.date?.full;
      if (aDate && bDate) {
        return new Date(aDate) > new Date(bDate) ? a : b;
      }
      if (aDate) {
        return a;
      }
      return b;
    },
    {
      apn: null,
      min: null,
      max: null,
      score: null,
      standard_deviation: null,
      year: null,
      total: null,
      land: null,
      improvements: null,
      source: null,
    }
  );
};
